import isEmpty from 'lodash/isEmpty';
import {
  createContext,
  type JSX,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ServiceCode } from '@/shared/types';

import { SERVICE_CODES } from '@/lib/consts';

import { FeatureName, UserService } from '@/api/types';

import { ServiceUnAvailable, ServiceUnavailableView } from '../../components/Forms/ServiceUnAvailable';
import { ServiceNames } from '../../config/services';
import { FeatureContext } from '../Features/FeatureContext';
import { ServicesContext } from '../Services/ServicesContext';

export enum Reason {
  NOT_ENOUGH_DATA = 'NO_DATA',
  DUPLICATED_APPLICATION = 'DUPLICATED_APPLICATION',
  SERVICE_NOT_AVAILABLE = 'SERVICE_NOT_AVAILABLE',
  MIN_USER_AGE = 'MIN_USER_AGE',
  UNAVAILABLE_MIN_USER_AGE = 'UNAVAILABLE_MIN_USER_AGE',
  REGISTERED_ADDRESS_ABROAD = 'REGISTERED_ADDRESS_ABROAD',
  RNOKPP_MISSING = 'RNOKPP_MISSING',
}

export interface ServiceAvailabilityContextInterface {
  isServiceAvailable: boolean;
  isServiceFeatureAvailable: boolean;
  disableCause: (props: ChangeAvailabilityProps) => void;
}

const ServiceAvailabilityContext = createContext<ServiceAvailabilityContextInterface>({
  isServiceAvailable: true,
  isServiceFeatureAvailable: true,
  disableCause: () => null,
});

interface Props extends PropsWithChildren {
  serviceCode: keyof typeof ServiceNames;
  skipAvailabilityValidation?: boolean;
}

type IsServiceAvailableInterface = boolean;
type FeatureAvailabiltyByFeatureName = Record<FeatureName, boolean>;

interface ChangeAvailabilityPropsWithCustomMessage {
  reason: Reason;
  message: Nullable<JSX.Element>;
}
interface ChangeAvailabilityPropsWithoutCustomMessage {
  reason: Reason.NOT_ENOUGH_DATA;
  message?: JSX.Element;
}
type ChangeAvailabilityProps = ChangeAvailabilityPropsWithCustomMessage | ChangeAvailabilityPropsWithoutCustomMessage;

const requiredFeaturesForServices: Partial<Record<FeatureName, ServiceCode[]>> = {
  [FeatureName.ERZ_SERVICES]: [
    SERVICE_CODES.PURCHASE,
    SERVICE_CODES.STORAGE_CARRYING,
    SERVICE_CODES.VALIDITY_PERIOD_PROLONGATION,
    SERVICE_CODES.TRANSPORT_ACROSS_BORDER,
    SERVICE_CODES.COLD_STEEL_TRANSPORTATION,
    SERVICE_CODES.COMMISSION_SALE,
    SERVICE_CODES.RESIDENCE_RELOCATION,
    SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION,
    SERVICE_CODES.COLD_STEEL_STORAGE_FOR_COLLECTION,
    SERVICE_CODES.CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION,
  ],
  [FeatureName.ERZ_REGISTRY]: [
    SERVICE_CODES.STORAGE_CARRYING,
    SERVICE_CODES.VALIDITY_PERIOD_PROLONGATION,
    SERVICE_CODES.TRANSPORT_ACROSS_BORDER,
    SERVICE_CODES.COLD_STEEL_TRANSPORTATION,
    SERVICE_CODES.COMMISSION_SALE,
    SERVICE_CODES.RESIDENCE_RELOCATION,
    SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION,
    SERVICE_CODES.COLD_STEEL_STORAGE_FOR_COLLECTION,
    SERVICE_CODES.CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION,
  ],
  [FeatureName.ERZ_TEMPORARY_RESIDENCE_RELOCATION]: [SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION],
  [FeatureName.ERZ_CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION]: [
    SERVICE_CODES.CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION,
  ],
  [FeatureName.IAC_SERVICES]: [SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT],
  [FeatureName.BIA_SERVICES]: [SERVICE_CODES.INSURANCE],
};

const featuresByCode = (code: ServiceCode): FeatureName[] => {
  const features = Object.entries(requiredFeaturesForServices)
    .filter(([, SERVICE_CODES]) => SERVICE_CODES.includes(code))
    .map(([feature]) => feature as FeatureName);

  return features;
};

export const ServiceAvailabilityProvider: React.FC<Props> = ({
  children,
  serviceCode,
  skipAvailabilityValidation = false,
}) => {
  const { isFeatureAvailable } = useContext(FeatureContext);
  const { services, isLoading: servicesIsLoading } = useContext(ServicesContext);
  const [customIsServiceAvailable, setCustomIsServiceAvailable] = useState<IsServiceAvailableInterface>(true);
  const [customUnAvailabilityReason, setCustomUnAvailabilityReason] = useState<Reason>();
  const [customUnAvailabilityMessage, setCustomUnAvailabilityMessage] = useState<JSX.Element>();

  const currentService = useMemo<Nullable<UserService>>(() => {
    if (servicesIsLoading || !services) return null;
    return services.find(({ code }: UserService) => code === serviceCode) || null;
  }, [serviceCode, services, servicesIsLoading]);

  const serviceFeatures = useMemo<FeatureName[]>(() => featuresByCode(serviceCode), [serviceCode]);

  if (!servicesIsLoading && services && !currentService) {
    throw new Response('Service unavailable', { status: 404 });
  }

  const featureAvailabiltyByFeatureName = useMemo<FeatureAvailabiltyByFeatureName>(
    () =>
      serviceFeatures.reduce(
        (acc, featureName) => ({
          ...acc,
          [featureName]: isFeatureAvailable(featureName),
        }),
        {} as FeatureAvailabiltyByFeatureName,
      ),
    [isFeatureAvailable, serviceFeatures],
  );

  const unavailableFeatureName = useMemo<Nullable<FeatureName>>(() => {
    for (const [featureName, isAvailable] of Object.entries(featureAvailabiltyByFeatureName)) {
      if (Object.prototype.hasOwnProperty.call(featureAvailabiltyByFeatureName, featureName)) {
        if (!isAvailable) return featureName as FeatureName;
      }
    }

    return null;
  }, [featureAvailabiltyByFeatureName]);

  const isServiceAvailable = useMemo<boolean>(
    () =>
      skipAvailabilityValidation ||
      (!!currentService && customIsServiceAvailable && !unavailableFeatureName && currentService?.isAvailable),
    [currentService?.isAvailable, customIsServiceAvailable, skipAvailabilityValidation, unavailableFeatureName],
  );

  const changeAvailability = useCallback(
    ({ reason, message }: ChangeAvailabilityProps): void => {
      setCustomIsServiceAvailable(false);
      setCustomUnAvailabilityReason(reason);
      message && setCustomUnAvailabilityMessage(message);
    },
    [setCustomIsServiceAvailable, setCustomUnAvailabilityReason],
  );

  useEffect(() => {
    if (!services?.length) return;

    if (!currentService || (!currentService?.isAvailable && !isServiceAvailable)) {
      changeAvailability({ reason: Reason.SERVICE_NOT_AVAILABLE, message: null });
    }
  }, [services, currentService, isServiceAvailable, changeAvailability]);

  return (
    <ServiceAvailabilityContext.Provider
      value={{
        isServiceAvailable: isServiceAvailable,
        isServiceFeatureAvailable: isEmpty(unavailableFeatureName),
        disableCause: changeAvailability,
      }}
    >
      {isServiceAvailable ? (
        children
      ) : (
        <ServiceUnAvailable
          causedBy={customUnAvailabilityReason || unavailableFeatureName}
          customMsg={customUnAvailabilityMessage}
          view={
            serviceCode === SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT
              ? ServiceUnavailableView.MODAL
              : ServiceUnavailableView.PAGE
          }
        />
      )}
    </ServiceAvailabilityContext.Provider>
  );
};

export default ServiceAvailabilityContext;
